import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface CustomWidgetSelectedItem {
  itemKey?: string;
  itemType: string;
  priorityOrder: number;
  config?: string;
}

export interface CustomWidgetItem {
  id?: number;
  title: string;
  slug: string;
  isActive: boolean;
  geo: string[];
  items: CustomWidgetSelectedItem[];

  createdAt?: string;
  updatedAt?: string;
}

export interface CustomWidgetsBunchPayload {
  ids: number[];
  isActive: number;
}

interface CustomWidgetState {
  customWidgetsList: CustomWidgetItem[];

  activePage: number;
  totalPages: number;
  totalItems: number;

  isListFetched: boolean;

  isItemFetched: boolean;
  isItemFetching: boolean;

  selectedItems: number[];

  payload: CustomWidgetItem;

  processingItemId?: CustomWidgetItem["id"];
  customWidgetItems: string[];

  isCustomWidgetItemsFetched: boolean;
  gamesOptions: any[];
  collectionsOptions: any[];
  bonusesOptions: any[];
  tournamentsOptions: any[];
  paymentMethodsOptions: any[];
  tagsOptions: any[];
  providersOptions: any[];

  isGamesFetched: boolean;
  isCollectionsFetched: boolean;
  isBonusesFetched: boolean;
  bonusesRequestId?: string;
  isTournamentsFetched: boolean;
  tournamentsRequestId?: string;
  isPaymentMethodsFetched: boolean;
  paymentMethodsRequestId?: string;
  isTagsFetched: boolean;
  isProvidersFetched: boolean;
}

interface GetListPayload {
  limit: number;
  offset: number;

  isActive?: number;
  title?: string;
  geo?: string[];

  order: {
    title: string;
    id: string;
    isActive: string;
  };
}

export const parseItemConfig = (config?: string) => {
  try {
    return config ? JSON.parse(config) : {};
  } catch {
    return {};
  }
};

const defPayload: CustomWidgetItem = {
  title: "",
  slug: "",
  isActive: true,
  geo: [],
  items: [],
};

const initialState: CustomWidgetState = {
  customWidgetsList: [],

  isListFetched: false,

  activePage: 1,
  totalItems: 0,
  totalPages: 0,

  selectedItems: [],

  isItemFetched: false,
  isItemFetching: false,

  payload: defPayload,
  customWidgetItems: [],

  isCustomWidgetItemsFetched: false,
  gamesOptions: [],
  collectionsOptions: [],
  bonusesOptions: [],
  tournamentsOptions: [],
  paymentMethodsOptions: [],
  tagsOptions: [],
  providersOptions: [],

  isGamesFetched: false,
  isCollectionsFetched: false,
  isBonusesFetched: false,
  bonusesRequestId: undefined,
  isTournamentsFetched: false,
  tournamentsRequestId: undefined,
  isPaymentMethodsFetched: false,
  paymentMethodsRequestId: undefined,
  isTagsFetched: false,
  isProvidersFetched: false,
};

export const createCustomWidget = createAsyncThunk<
  number,
  CustomWidgetItem,
  { rejectValue: ApiErrorPayload }
>("customized/create", async (payload, { rejectWithValue }) => {
  try {
    const result = await instance.post(apiRoutes.customizedCreate, {
      ...payload,
      isActive: payload.isActive ? 1 : 0,
    });

    return result.data.id;
  } catch (error: any) {
    const apiError = error?.data?.error;
    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateCustomWidget = createAsyncThunk<
  boolean,
  CustomWidgetItem,
  { rejectValue: ApiErrorPayload }
>("customized/update", async (payload, { rejectWithValue }) => {
  try {
    await instance.put(`${apiRoutes.customizedUpdate}/${payload.id}`, {
      ...payload,
      isActive: payload.isActive ? 1 : 0,
    });

    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError && apiError.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateCustomWidgetsBunch = createAsyncThunk(
  "customized/update-bunch",
  async (data: CustomWidgetsBunchPayload) => {
    try {
      await instance.put(apiRoutes.customizedUpdateBunch, {
        ids: data.ids,
        isActive: data.isActive,
      });

      return true;
    } catch (error) {
      return false;
    }
  },
);

export const getCustomWidgetsList = createAsyncThunk(
  "customized/list",
  async (data: GetListPayload) => {
    try {
      const params = new URLSearchParams();

      params.append("limit", String(data.limit));
      params.append("offset", String(data.offset));

      if (data.title) {
        params.append("title", data.title);
      }

      if (data.isActive !== undefined) {
        params.append("isActive", String(data.isActive));
      }

      if (data.geo?.length) {
        data.geo.forEach((item) => {
          params.append("geo", item);
        });
      }

      const result = await instance.get(
        `${apiRoutes.customizedList}?${params.toString()}`,
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getCustomWidgetGames = createAsyncThunk(
  "customized/games",
  async (search?: string) => {
    try {
      const result = await instance.post(apiRoutes.gamesList, {
        limit: 100,
        offset: 0,
        isActive: 1,
        gameTitle: search ?? undefined,
      });

      return result.data;
    } catch {
      return undefined;
    }
  },
);

export const getCustomWidgetCollections = createAsyncThunk(
  "customized/collections",
  async (search?: string) => {
    try {
      const result = await instance.get(apiRoutes.collectionsList, {
        params: {
          search,
        },
      });

      return result.data;
    } catch {
      return undefined;
    }
  },
);

interface GetCustomWidgetBonusesPayload {
  search?: string;
}

export const getCustomWidgetBonuses = createAsyncThunk(
  "customized/bonuses",
  async ({ search }: GetCustomWidgetBonusesPayload = {}) => {
    try {
      const result = await instance.post(apiRoutes.bonusesList, {
        limit: 100,
        offset: 0,
        statusId: 3,
        currencyIds: ["USD"],
        title: search?.trim() || undefined,
        order: {
          id: "desc",
          type: "desc",
          title: "asc",
        },
      });

      return result.data;
    } catch {
      return undefined;
    }
  },
);

interface GetCustomWidgetTournamentsPayload {
  search?: string;
}

export const getCustomWidgetTournaments = createAsyncThunk(
  "customized/tournaments",
  async ({ search }: GetCustomWidgetTournamentsPayload = {}) => {
    try {
      const result = await instance.post(apiRoutes.tournamentsList, {
        limit: 100,
        offset: 0,
        isActive: 1,
        title: search?.trim() || undefined,
        order: {
          currentMembersCount: "desc",
          title: "asc",
        },
      });

      return result.data;
    } catch {
      return undefined;
    }
  },
);

interface GetCustomWidgetPaymentMethodsPayload {
  search?: string;
}

export const getCustomWidgetPaymentMethods = createAsyncThunk(
  "customized/payment-methods",
  async ({ search }: GetCustomWidgetPaymentMethodsPayload = {}) => {
    try {
      const result = await instance.post(apiRoutes.paymentMethodList, {
        limit: 100,
        offset: 0,
        isActive: 1,
        transactionType: "deposit",
        title: search?.trim() || undefined,
        order: {
          isActive: "asc",
          id: "asc",
        },
      });

      return result.data;
    } catch {
      return undefined;
    }
  },
);

export const getCustomWidgetTags = createAsyncThunk(
  "customized/tags",
  async (search?: string) => {
    try {
      const result = await instance.get(apiRoutes.tagsList, {
        params: {
          search,
        },
      });

      return result.data;
    } catch {
      return undefined;
    }
  },
);

export const getCustomWidgetItem = createAsyncThunk(
  "customized/item",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.customizedItem}/${id}`);

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getCustomWidgetItems = createAsyncThunk(
  "customized/items",
  async () => {
    try {
      const result = await instance.get(apiRoutes.customizedItems);

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getCustomWidgetProviders = createAsyncThunk(
  "customized/providers",
  async () => {
    try {
      const result = await instance.post(apiRoutes.providersList, {
        limit: 100,
        offset: 0,
        isActive: 1,
      });

      return result.data;
    } catch {
      return undefined;
    }
  },
);

export const deleteCustomWidget = createAsyncThunk(
  "customized/delete",
  async (id: string | number) => {
    try {
      const result = await instance.delete(
        `${apiRoutes.customizedDelete}/${id}`,
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const cmsCustomizedSlice = createSlice({
  name: "cms-customized",

  initialState,

  reducers: {
    handleUpdateFields: (
      state: CustomWidgetState,
      action: PayloadAction<Partial<CustomWidgetItem>>,
    ) => {
      state.payload = {
        ...state.payload,
        ...action.payload,
      };
    },

    handleClearFields: (state: CustomWidgetState) => {
      state.payload = defPayload;
      state.isItemFetched = false;
    },

    handlePaginate: (
      state: CustomWidgetState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;

      state.isListFetched = false;

      state.selectedItems = [];
    },

    handleSelectItem: (
      state: CustomWidgetState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },

    handleSelectAll: (state: CustomWidgetState) => {
      if (state.selectedItems.length === state.customWidgetsList.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.customWidgetsList.map((el) => el.id ?? 0);
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getCustomWidgetsList.fulfilled, (state, action) => {
      state.isListFetched = true;

      state.customWidgetsList = action.payload?.items ?? [];

      state.totalItems = action.payload?.totalItems ?? 1;

      state.totalPages = action.payload?.totalPages ?? 1;
    });

    builder.addCase(createCustomWidget.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(createCustomWidget.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });

    builder.addCase(createCustomWidget.rejected, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(updateCustomWidget.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });

    builder.addCase(updateCustomWidget.fulfilled, (state, action) => {
      state.customWidgetsList = state.customWidgetsList.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.meta.arg)
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });

    builder.addCase(updateCustomWidget.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });

    builder.addCase(updateCustomWidgetsBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getCustomWidgetItem.pending, (state) => {
      state.isItemFetched = false;
    });

    builder.addCase(getCustomWidgetItem.fulfilled, (state, action) => {
      state.isItemFetched = true;

      state.payload = {
        ...action.payload,
        isActive: !!action.payload?.isActive,
      };
    });

    builder.addCase(deleteCustomWidget.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || 0;
    });

    builder.addCase(deleteCustomWidget.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });

    builder.addCase(deleteCustomWidget.rejected, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getCustomWidgetItems.pending, (state) => {
      state.isCustomWidgetItemsFetched = false;
    });

    builder.addCase(getCustomWidgetItems.fulfilled, (state, action) => {
      state.isCustomWidgetItemsFetched = true;

      state.customWidgetItems = action.payload?.items ?? [];
    });

    builder.addCase(getCustomWidgetItems.rejected, (state) => {
      state.isCustomWidgetItemsFetched = true;
    });
    builder.addCase(getCustomWidgetGames.pending, (state) => {
      state.isGamesFetched = false;
    });

    builder.addCase(getCustomWidgetGames.fulfilled, (state, action) => {
      state.isGamesFetched = true;

      //  state.gamesOptions = action.payload?.items ?? [];
      state.gamesOptions =
        action.payload?.items?.map((item: any) => ({
          value: item.id,
          label: item.title,
        })) ?? [];
    });

    builder.addCase(getCustomWidgetGames.rejected, (state) => {
      state.isGamesFetched = true;
    });
    builder.addCase(getCustomWidgetCollections.pending, (state) => {
      state.isCollectionsFetched = false;
    });

    builder.addCase(getCustomWidgetCollections.fulfilled, (state, action) => {
      state.isCollectionsFetched = true;

      state.collectionsOptions =
        action.payload?.items?.map((item: any) => ({
          value: item.slug,
          label: item.title,
        })) ?? [];
    });

    builder.addCase(getCustomWidgetCollections.rejected, (state) => {
      state.isCollectionsFetched = true;
    });
    builder.addCase(getCustomWidgetBonuses.pending, (state, action) => {
      state.isBonusesFetched = false;
      state.bonusesRequestId = action.meta.requestId;
    });

    builder.addCase(getCustomWidgetBonuses.fulfilled, (state, action) => {
      if (state.bonusesRequestId !== action.meta.requestId) {
        return;
      }

      state.isBonusesFetched = true;
      state.bonusesRequestId = undefined;

      const options =
        action.payload?.items?.map((item: any) => ({
          value: item.id,
          label: item.title,
        })) ?? [];

      const optionIds = new Set<number | string>();
      state.bonusesOptions = options.filter((item: any) => {
        if (optionIds.has(item.value)) {
          return false;
        }

        optionIds.add(item.value);
        return true;
      });
    });

    builder.addCase(getCustomWidgetBonuses.rejected, (state, action) => {
      if (state.bonusesRequestId !== action.meta.requestId) {
        return;
      }

      state.isBonusesFetched = true;
      state.bonusesRequestId = undefined;
    });
    builder.addCase(getCustomWidgetTournaments.pending, (state, action) => {
      state.isTournamentsFetched = false;
      state.tournamentsRequestId = action.meta.requestId;
    });

    builder.addCase(getCustomWidgetTournaments.fulfilled, (state, action) => {
      if (state.tournamentsRequestId !== action.meta.requestId) {
        return;
      }

      state.isTournamentsFetched = true;
      state.tournamentsRequestId = undefined;

      const optionIds = new Set<number>();
      state.tournamentsOptions =
        action.payload?.items
          ?.map((item: any) => ({
            value: item.id,
            label: item.title,
          }))
          .filter((item: any) => {
            if (optionIds.has(item.value)) {
              return false;
            }

            optionIds.add(item.value);
            return true;
          }) ?? [];
    });

    builder.addCase(getCustomWidgetTournaments.rejected, (state, action) => {
      if (state.tournamentsRequestId !== action.meta.requestId) {
        return;
      }

      state.isTournamentsFetched = true;
      state.tournamentsRequestId = undefined;
    });
    builder.addCase(getCustomWidgetPaymentMethods.pending, (state, action) => {
      state.isPaymentMethodsFetched = false;
      state.paymentMethodsRequestId = action.meta.requestId;
    });

    builder.addCase(
      getCustomWidgetPaymentMethods.fulfilled,
      (state, action) => {
        if (state.paymentMethodsRequestId !== action.meta.requestId) {
          return;
        }

        state.isPaymentMethodsFetched = true;
        state.paymentMethodsRequestId = undefined;

        const optionIds = new Set<number>();
        state.paymentMethodsOptions =
          action.payload?.items
            ?.filter(
              (item: any) =>
                (item.isActive === true || Number(item.isActive) === 1) &&
                item.transactionType !== "withdrawal",
            )
            .map((item: any) => ({
              value: item.id,
              label: item.title,
            }))
            .filter((item: any) => {
              if (optionIds.has(item.value)) {
                return false;
              }

              optionIds.add(item.value);
              return true;
            }) ?? [];
      },
    );

    builder.addCase(
      getCustomWidgetPaymentMethods.rejected,
      (state, action) => {
        if (state.paymentMethodsRequestId !== action.meta.requestId) {
          return;
        }

        state.isPaymentMethodsFetched = true;
        state.paymentMethodsRequestId = undefined;
      },
    );

    builder.addCase(getCustomWidgetTags.pending, (state) => {
      state.isTagsFetched = false;
    });

    builder.addCase(getCustomWidgetTags.fulfilled, (state, action) => {
      state.isTagsFetched = true;

      state.tagsOptions =
        action.payload?.items?.map((item: any) => ({
          value: item.slug,
          label: item.title,
        })) ?? [];
    });

    builder.addCase(getCustomWidgetTags.rejected, (state) => {
      state.isTagsFetched = true;
    });
    builder.addCase(getCustomWidgetProviders.pending, (state) => {
      state.isProvidersFetched = false;
    });
    builder.addCase(getCustomWidgetProviders.fulfilled, (state, action) => {
      state.isProvidersFetched = true;

      state.providersOptions =
        action.payload?.items?.map((item: any) => ({
          value: item.slug,
          label: item.title,
        })) ?? [];
    });
    builder.addCase(getCustomWidgetProviders.rejected, (state) => {
      state.isProvidersFetched = true;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleClearFields,
  handleUpdateFields,
} = cmsCustomizedSlice.actions;
